<script setup>
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';
import TeamsApi from 'dashboard/api/teams';

import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const containerRef = useTemplateRef('containerRef');
const [showDropdown, toggleDropdown] = useToggle(false);
const isUpdating = ref(false);
const isLoadingTeam = ref(false);
const teamMemberIds = ref(new Set());

const assignableAgentsGetter = useMapGetter(
  'inboxAssignableAgents/getAssignableAgents'
);
const assignableAgentsUiFlags = useMapGetter(
  'inboxAssignableAgents/getUIFlags'
);

const assignedAgent = computed(() => props.chat.meta?.assignee || null);
const assignedTeam = computed(() => props.chat.meta?.team || null);
const isLoading = computed(
  () => assignableAgentsUiFlags.value.isFetching || isLoadingTeam.value
);
const assignableAgents = computed(() =>
  assignableAgentsGetter.value(props.inbox.id)
);

const agentMenuItem = agent => ({
  action: 'assign',
  value: agent.id,
  label: agent.name,
  thumbnail: {
    name: agent.name,
    src: agent.thumbnail,
  },
  isSelected: assignedAgent.value?.id === agent.id,
});

const teamAgents = computed(() =>
  assignableAgents.value
    .filter(agent => teamMemberIds.value.has(agent.id))
    .map(agentMenuItem)
);

const otherAgents = computed(() =>
  assignableAgents.value
    .filter(agent => !teamMemberIds.value.has(agent.id))
    .map(agentMenuItem)
);

const menuSections = computed(() => {
  const sections = [];

  if (teamAgents.value.length) {
    sections.push({
      title: t('CONVERSATION.HEADER.ASSIGNEE_TEAM', {
        team: assignedTeam.value.name,
      }),
      items: teamAgents.value,
    });
  }

  if (otherAgents.value.length) {
    sections.push({
      title: t('CONVERSATION.HEADER.ASSIGNEE_INBOX_TEAMMATES'),
      items: otherAgents.value,
    });
  }

  sections.push({
    items: [
      {
        action: 'assign',
        value: null,
        label: t('CONVERSATION.HEADER.ASSIGNEE_UNASSIGNED'),
        icon: 'i-lucide-user-x',
        isSelected: !assignedAgent.value,
      },
    ],
  });

  return sections;
});

const closeDropdown = () => toggleDropdown(false);

const loadAssignableAgents = () =>
  store.dispatch('inboxAssignableAgents/fetch', [props.inbox.id]);

const loadTeamMembers = async () => {
  if (!assignedTeam.value?.id) {
    teamMemberIds.value = new Set();
    return;
  }

  isLoadingTeam.value = true;
  try {
    const { data } = await TeamsApi.getAgents({
      teamId: assignedTeam.value.id,
    });
    teamMemberIds.value = new Set(data.map(member => member.id));
  } finally {
    isLoadingTeam.value = false;
  }
};

const handleToggle = async () => {
  const willOpen = !showDropdown.value;
  toggleDropdown();

  if (willOpen) {
    try {
      await Promise.all([loadAssignableAgents(), loadTeamMembers()]);
    } catch (error) {
      useAlert(t('CONVERSATION.HEADER.ASSIGNEE_LOAD_FAILED'));
    }
  }
};

const handleAssignment = async ({ value }) => {
  if (isUpdating.value || assignedAgent.value?.id === value) {
    closeDropdown();
    return;
  }

  isUpdating.value = true;
  try {
    const response = await ConversationApi.assignAgent({
      conversationId: props.chat.id,
      agentId: value,
    });
    const assignee = value ? response.data : null;
    await store.dispatch('setCurrentChatAssignee', {
      conversationId: props.chat.id,
      assignee,
    });
    useAlert(
      assignee
        ? t('CONVERSATION.HEADER.ASSIGNEE_UPDATED', {
            agent: assignee.name,
          })
        : t('CONVERSATION.HEADER.ASSIGNEE_REMOVED')
    );
    closeDropdown();
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div
    ref="containerRef"
    v-on-click-outside="[closeDropdown, { ignore: [containerRef] }]"
    class="relative"
  >
    <Button
      v-tooltip="
        assignedAgent
          ? $t('CONVERSATION.HEADER.REASSIGN_AGENT')
          : $t('CONVERSATION.HEADER.ASSIGN_AGENT')
      "
      size="sm"
      variant="outline"
      color="slate"
      :class="
        compact
          ? '!size-7 !gap-0 !rounded-md !p-0 bg-n-solid-1 shadow-sm'
          : 'max-w-44 !gap-1.5 !px-2'
      "
      :is-loading="isUpdating"
      :aria-label="$t('CONVERSATION.HEADER.ASSIGNEE_MENU')"
      @click="handleToggle"
    >
      <Avatar
        v-if="assignedAgent"
        :src="assignedAgent.thumbnail"
        :name="assignedAgent.name"
        :status="assignedAgent.availability_status"
        :size="compact ? 18 : 20"
        rounded-full
      />
      <Icon v-else icon="i-lucide-user-plus" class="size-4 shrink-0" />
      <span v-if="!compact" class="hidden min-w-0 truncate text-sm sm:block">
        {{
          assignedAgent
            ? assignedAgent.name
            : $t('CONVERSATION.HEADER.ASSIGN_AGENT')
        }}
      </span>
      <Icon
        v-if="!compact"
        icon="i-lucide-chevron-down"
        class="size-3.5 shrink-0 transition-transform"
        :class="{ 'rotate-180': showDropdown }"
      />
    </Button>

    <DropdownMenu
      v-if="showDropdown"
      :menu-sections="menuSections"
      :is-loading="isLoading"
      show-search
      :search-placeholder="$t('CONVERSATION.HEADER.ASSIGNEE_SEARCH')"
      class="top-full mt-1 w-64 max-h-72 !bg-n-solid-1 ltr:right-0 rtl:left-0"
      @action="handleAssignment"
    />
  </div>
</template>
